import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { adminPath } from '../../../lib/adminPaths';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { trpc } from '../../../lib/api';
import { withDevModalFilePath } from '../../../components/common';
import { getMutationPendingState } from '../../../utils/trpcMutation';

const { Modal } = withDevModalFilePath('client/src/pages/admin/users/UserDetailPage.tsx');
const { Title, Text } = Typography;
const { Option } = Select;

type UserRole = 'admin' | 'manager' | 'user' | 'viewer';

const roleLabels: Record<UserRole, string> = {
  admin: '管理者',
  manager: 'マネージャー',
  user: 'ユーザー',
  viewer: '閲覧者',
};

const roleColors: Record<UserRole, string> = {
  admin: 'red',
  manager: 'orange',
  user: 'blue',
  viewer: 'default',
};

interface UpdateUserFormValues {
  name: string;
  email: string;
  isActive?: boolean;
  role?: UserRole;
}

interface ResetPasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY/MM/DD HH:mm');
};

export function UserDetailPage() {
  const { message } = App.useApp();
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const {
    data: user,
    isLoading,
    error,
    refetch: refetchUser,
  } = trpc.admin.getUserById.useQuery(userId ?? '', {
    enabled: !!userId,
  });

  const updateUserMutation = trpc.admin.updateUser.useMutation({
    onSuccess: () => {
      message.success('ユーザー情報を更新しました');
      setIsEditModalVisible(false);
      editForm.resetFields();
      refetchUser();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'ユーザー情報の更新に失敗しました';
      message.error(errorMessage);
    },
  });

  const resetPasswordMutation = trpc.admin.resetPassword.useMutation({
    onSuccess: () => {
      message.success('パスワードをリセットしました');
      setIsPasswordModalVisible(false);
      passwordForm.resetFields();
    },
    onError: () => {
      message.error('パスワードのリセットに失敗しました');
    },
  });

  const handleUpdate = (values: UpdateUserFormValues) => {
    if (!userId) return;
    updateUserMutation.mutate({
      id: userId,
      ...values,
    });
  };

  const handlePasswordReset = (values: ResetPasswordFormValues) => {
    if (!userId) return;
    resetPasswordMutation.mutate({
      userId: userId,
      newPassword: values.newPassword,
    });
  };

  const openEditModal = () => {
    if (user) {
      editForm.setFieldsValue({
        name: user.name,
        email: user.email,
        isActive: user.isActive,
        role: user.role,
      });
      setIsEditModalVisible(true);
    }
  };

  if (!userId) {
    return (
      <Result
        status="error"
        title="ユーザーIDが指定されていません"
        extra={
          <Button type="primary" onClick={() => navigate(adminPath('users'))}>
            ユーザー一覧へ戻る
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Spin tip="読み込み中..." size="large">
          <div style={{ minHeight: 120 }} />
        </Spin>
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="ユーザー情報の取得に失敗しました"
        subTitle={error.message}
        extra={
          <Button type="primary" onClick={() => navigate(adminPath('users'))}>
            ユーザー一覧へ戻る
          </Button>
        }
      />
    );
  }

  if (!user) {
    return (
      <Result
        status="404"
        title="ユーザーが見つかりません"
        subTitle="指定されたユーザーは存在しないか、アクセスできません。"
        extra={
          <Button type="primary" onClick={() => navigate(adminPath('users'))}>
            ユーザー一覧へ戻る
          </Button>
        }
      />
    );
  }

  const role = user.role as UserRole;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Space align="center" size="middle">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(adminPath('users'))}>
              ユーザー一覧へ
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {user.name}
            </Title>
          </Space>
          <Space size="small" style={{ marginTop: 8 }}>
            {user.deletedAt ? (
              <Tag color="volcano">削除済み</Tag>
            ) : (
              <Tag color={user.isActive ? 'green' : 'red'}>
                {user.isActive ? '稼働中' : '停止'}
              </Tag>
            )}
            <Tag color={roleColors[role] || 'default'}>
              {roleLabels[role] || role}
            </Tag>
          </Space>
        </div>

        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={openEditModal}
            disabled={!!user.deletedAt}
          >
            ユーザー情報を編集
          </Button>
          <Button
            icon={<KeyOutlined />}
            onClick={() => setIsPasswordModalVisible(true)}
            disabled={!!user.deletedAt}
          >
            パスワードリセット
          </Button>
        </Space>

        <Row gutter={24}>
          <Col span={12}>
            <Card title="基本情報">
              <Descriptions
                column={1}
                bordered
                size="small"
                styles={{ label: { width: '35%' } }}
              >
                <Descriptions.Item label="名前">
                  {user.name}
                </Descriptions.Item>
                <Descriptions.Item label="メールアドレス">
                  {user.email}
                </Descriptions.Item>
                <Descriptions.Item label="ロール">
                  <Tag color={roleColors[role] || 'default'}>
                    {roleLabels[role] || role}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ステータス">
                  {user.deletedAt ? (
                    <Tag color="volcano">削除済み</Tag>
                  ) : (
                    <Tag color={user.isActive ? 'green' : 'red'}>
                      {user.isActive ? '稼働中' : '停止'}
                    </Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="所属情報">
              <Descriptions
                column={1}
                bordered
                size="small"
                styles={{ label: { width: '35%' } }}
              >
                <Descriptions.Item label="会社">
                  {user.companyId ? (
                    <Link to={adminPath(`companies/${user.companyId}`)}>
                      {user.companyName || user.companyId}
                    </Link>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="最終ログイン">
                  {formatDateTime(user.lastLoginAt)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Card title="監査情報">
              <Descriptions
                column={1}
                bordered
                size="small"
                styles={{ label: { width: '35%' } }}
              >
                <Descriptions.Item label="作成日時">
                  {formatDateTime(user.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="更新日時">
                  {formatDateTime(user.updatedAt)}
                </Descriptions.Item>
                <Descriptions.Item label="削除日時">
                  {user.deletedAt ? formatDateTime(user.deletedAt) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="削除理由">
                  {user.deletionReason ?? '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="アカウント状態">
              <Descriptions
                column={1}
                bordered
                size="small"
                styles={{ label: { width: '35%' } }}
              >
                <Descriptions.Item label="無効化日時">
                  {user.deactivatedAt ? formatDateTime(user.deactivatedAt) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="無効化理由">
                  {user.deactivatedReason ?? '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </Space>

      {/* ユーザー編集モーダル */}
      <Modal
        title="ユーザー情報編集"
        open={isEditModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
        }}
        confirmLoading={getMutationPendingState(updateUserMutation)}
        okText="更新"
        cancelText="キャンセル"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="name"
            label="名前"
            rules={[{ required: true, message: '名前を入力してください' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="アクティブ"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
          <Form.Item
            name="role"
            label="ロール"
            rules={[{ required: true, message: 'ロールを選択してください' }]}
          >
            <Select placeholder="ロールを選択">
              <Option value="admin">管理者</Option>
              <Option value="manager">マネージャー</Option>
              <Option value="user">ユーザー</Option>
              <Option value="viewer">閲覧者</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* パスワードリセットモーダル */}
      <Modal
        title="パスワードリセット"
        open={isPasswordModalVisible}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        confirmLoading={getMutationPendingState(resetPasswordMutation)}
        okText="リセット"
        cancelText="キャンセル"
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordReset}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              ユーザー: {user.name} ({user.email})
            </Text>
          </div>
          <Form.Item
            name="newPassword"
            label="新しいパスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上である必要があります' },
            ]}
          >
            <Input.Password placeholder="8文字以上のパスワード" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="パスワード確認"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'パスワードを再入力してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="パスワードを再入力" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
